import { getErrorMessage, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import HorizontalSplitPane from "@entrilia/shared/components/HorizontalSplitPane";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { memo, useState } from "react";
import adminApi from "../../../api/adminApi";
import useUserContext from "../../../context/useUserContext";
import type {
  AssistantAction,
  AssistantAttachment,
  AssistantContentRef,
  AssistantFile,
  AssistantMappingChoices,
} from "../../../api/types/assistantTypes";
import Chat from "./Chat";
import { addQueuedRunAction, buildChatItems, isRunPending } from "./chatState";
import { getContentKey, isSameContent } from "./content/assistantContent";
import ContentPanel from "./content/ContentPanel";
import useAssistantChat from "./useAssistantChat";

interface Props {
  threadId: string;
  onUpdatesRead: () => void;
  onContentOpen: () => void;
}

const LOST_ACCESS_MESSAGE = "You no longer have access to the Assistant.";

const startRun = withErrorHandling(adminApi.startAssistantRun);
const stopRun = withErrorHandling(adminApi.stopAssistantRun);
const approveAction = withErrorHandling(adminApi.approveAssistantAction);
const rejectAction = withErrorHandling(adminApi.rejectAssistantAction);
const uploadAttachment = withErrorHandling(adminApi.uploadAssistantAttachment);

const AssistantChatSession = ({ threadId, onUpdatesRead, onContentOpen }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const { refreshUserData } = useUserContext();

  const [isStarting, setIsStarting] = useState(false);
  const [isDeciding, setIsDeciding] = useState(false);
  const [openContent, setOpenContent] = useState<AssistantContentRef>();
  const [isContentExpanded, setIsContentExpanded] = useState(false);

  const { state, chatError, applyAction, fetchRunEvents, reloadChat } = useAssistantChat(threadId, onUpdatesRead);

  if (chatError) {
    logError(chatError, "[AssistantChatSession] getAssistantChat");
    return <DataLoadingFailed title="This chat could not be opened" />;
  }

  if (!state) {
    return <InlineLoader />;
  }

  const handleLostAccess = () => {
    sendNotificationError(LOST_ACCESS_MESSAGE);
    refreshUserData();
  };

  const handleRequestError = (error: ApiError) => {
    if (error.code === 403) {
      handleLostAccess();
      return;
    }

    sendNotificationError(error.message);
    reloadChat();
  };

  const handleSubmitPrompt = async (messageText: string, attachments: AssistantAttachment[]): Promise<boolean> => {
    setIsStarting(true);
    const [response, error] = await startRun({
      threadId,
      messageTextUserInput: messageText,
      attachmentIds: attachments.map((attachment) => attachment.attachmentId),
    });
    setIsStarting(false);

    if (error) {
      logError(error, "[AssistantChatSession] startAssistantRun");
      handleRequestError(error);
      return false;
    }

    applyAction(addQueuedRunAction(response.runId, messageText, attachments));
    fetchRunEvents(response.runId);
    return true;
  };

  const handleStopGenerating = async () => {
    const lastRun = state.runs.at(-1);
    if (!lastRun) {
      return;
    }

    const [, error] = await stopRun(lastRun.runId);
    if (error) {
      logError(error, "[AssistantChatSession] stopAssistantRun");
      sendNotificationError(error.message);
    }

    fetchRunEvents(lastRun.runId);
  };

  const handleActionDecision = async (
    action: AssistantAction,
    approve: boolean,
    mappingChoices?: AssistantMappingChoices
  ) => {
    setIsDeciding(true);

    const request = { threadId, turnId: action.turnId, callId: action.callId };
    const [response, error] = approve
      ? await approveAction({ ...request, choicesUserInput: mappingChoices })
      : await rejectAction(request);

    if (error) {
      setIsDeciding(false);
      logError(error, `[AssistantChatSession] ${approve ? "approveAssistantAction" : "rejectAssistantAction"}`);
      handleRequestError(error);
      return;
    }

    await fetchRunEvents(response.runId);
    setIsDeciding(false);
  };

  const handleUploadFile = async (file: File, signal: AbortSignal) => {
    const [attachment, error] = await uploadAttachment(threadId, file, signal);
    if (error) {
      if (error.code === 403) {
        handleLostAccess();
        throw new Error(LOST_ACCESS_MESSAGE);
      }

      throw new Error(error.message || "The file could not be attached.");
    }

    return attachment;
  };

  const handleOpenFile = async (file: AssistantFile) => {
    if (!file.id) {
      return;
    }

    try {
      const response = await adminApi.getAssistantAttachmentDownload(threadId, file.id);
      if (!response.success) {
        sendNotificationError(response.error?.message ?? "The file could not be opened.");
        return;
      }
      await downloadFileFromUrl(response.data.url);
    } catch (error) {
      logError(error, "[AssistantChatSession] getAssistantAttachmentDownload");
      sendNotificationError(getErrorMessage(error));
    }
  };

  const handleToggleContent = (content: AssistantContentRef) => {
    if (isSameContent(content, openContent)) {
      setOpenContent(undefined);
      return;
    }
    setOpenContent(content);
    onContentOpen();
  };

  const handleCloseContent = () => {
    setOpenContent(undefined);
    setIsContentExpanded(false);
  };

  const items = buildChatItems(state);
  const lastRun = state.runs.at(-1);
  const isLastRunPending = !!lastRun && isRunPending(lastRun.status);

  return (
    <HorizontalSplitPane
      leftPane={
        <Chat
          items={items}
          promptDisabled={state.isReadOnly || isLastRunPending || isStarting || isDeciding}
          isReadOnly={state.isReadOnly}
          stopDisabled={!!lastRun?.stopRequested}
          openContent={openContent}
          onUploadFile={handleUploadFile}
          onSubmit={handleSubmitPrompt}
          onStopGenerating={handleStopGenerating}
          onActionDecision={handleActionDecision}
          onOpenFile={handleOpenFile}
          onToggleContent={handleToggleContent}
        />
      }
      rightPane={
        openContent ? (
          <ContentPanel
            key={getContentKey(openContent)}
            content={openContent}
            isExpanded={isContentExpanded}
            onToggleExpand={() => setIsContentExpanded((current) => !current)}
            onClose={handleCloseContent}
          />
        ) : null
      }
      isRightPaneCollapsed={!openContent}
      isLeftPaneCollapsed={!!openContent && isContentExpanded}
      minLeftWidth={420}
      minRightWidth={480}
      containerSx={{ flex: 1, minWidth: 0 }}
      leftPaneSx={{ display: "flex" }}
      rightPaneSx={{ display: "flex" }}
    />
  );
};

export default memo(AssistantChatSession);
